import React, { useState } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Typography, Button, Badge } from 'antd';
import {
    DashboardOutlined,
    UserOutlined,
    ShopOutlined,
    ShoppingCartOutlined,
    EnvironmentOutlined,
    AppstoreOutlined,
    FileTextOutlined,
    SettingOutlined,
    BarChartOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    LogoutOutlined,
    BellOutlined,
    CreditCardOutlined,
    DatabaseOutlined,
    ApiOutlined,
    PictureOutlined,
    EyeOutlined,
    StarOutlined,
    MailOutlined,
    BugOutlined,
    CloudDownloadOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const { Header, Sider, Content } = Layout;

function AdminLayout() {
    const [collapsed, setCollapsed] = useState(false);
    const location = useLocation();
    const { user, logout } = useAuth();

    const menuItems = [
        {
            key: 'overview',
            label: 'Overview',
            type: 'group',
        },
        {
            key: '/admin/dashboard',
            icon: <DashboardOutlined />,
            label: <Link to="/admin/dashboard">Dashboard</Link>,
        },
        {
            key: 'management',
            label: 'Management',
            type: 'group',
        },
        {
            key: '/admin/users',
            icon: <UserOutlined />,
            label: <Link to="/admin/users">Users</Link>,
        },
        {
            key: '/admin/vendors',
            icon: <ShopOutlined />,
            label: <Link to="/admin/vendors">Vendors</Link>,
        },
        {
            key: '/admin/bookings',
            icon: <ShoppingCartOutlined />,
            label: <Link to="/admin/bookings">Bookings</Link>,
        },
        {
            key: '/admin/leads',
            icon: <UserOutlined />,
            label: <Link to="/admin/leads">Leads</Link>,
        },
        {
            key: 'content',
            label: 'Content',
            type: 'group',
        },
        {
            key: '/admin/content',
            icon: <FileTextOutlined />,
            label: <Link to="/admin/content">Content</Link>,
        },
        {
            key: '/admin/hero-banners',
            icon: <PictureOutlined />,
            label: <Link to="/admin/hero-banners">Hero & Banners</Link>,
        },
        {
            key: '/admin/menus',
            icon: <FileTextOutlined />,
            label: <Link to="/admin/menus">Menu Moderation</Link>,
        },
        // Consolidated: Testimonials, Features, Articles managed in Content
        {
            key: 'system',
            label: 'System',
            type: 'group',
        },
        {
            key: '/admin/analytics',
            icon: <BarChartOutlined />,
            label: <Link to="/admin/analytics">Analytics</Link>,
        },
        {
            key: '/admin/notifications',
            icon: <BellOutlined />,
            label: <Link to="/admin/notifications">Notifications</Link>,
        },
        {
            key: '/admin/integrations',
            icon: <ApiOutlined />,
            label: <Link to="/admin/integrations">Integrations</Link>,
        },
        {
            key: 'email',
            label: 'Email',
            type: 'group',
        },
        {
            key: '/admin/email-configuration',
            icon: <MailOutlined />,
            label: <Link to="/admin/email-configuration">Email Config</Link>,
        },
        {
            key: '/admin/email-templates',
            icon: <MailOutlined />,
            label: <Link to="/admin/email-templates">Email Templates</Link>,
        },
        {
            key: '/admin/smtp',
            icon: <MailOutlined />,
            label: <Link to="/admin/smtp">SMTP</Link>,
        },
        {
            key: 'tools',
            label: 'Tools',
            type: 'group',
        },
        {
            key: '/admin/logs',
            icon: <BugOutlined />,
            label: <Link to="/admin/logs">Logs</Link>,
        },
        {
            key: '/admin/backups',
            icon: <CloudDownloadOutlined />,
            label: <Link to="/admin/backups">Backup & Restore</Link>,
        },
        {
            key: '/admin/settings',
            icon: <SettingOutlined />,
            label: <Link to="/admin/settings">Settings</Link>,
        },
    ];

    const userMenuItems = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Profile',
        },
        {
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            onClick: logout,
        },
    ];

    return (
        <Layout className="min-h-screen bg-gray-50">
            <Sider 
                trigger={null} 
                collapsible 
                collapsed={collapsed}
                className="bg-white shadow-lg border-r border-gray-200"
                width={280}
                style={{
                    background: '#ffffff',
                    borderRight: '1px solid #e5e7eb'
                }}
            >
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-red-500">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg">
                            <span className="text-orange-600 font-bold text-lg">SS</span>
                        </div>
                        {!collapsed && (
                            <div>
                                <Typography.Title level={4} className="!mb-0 !text-white">
                                    Spoon & Spice
                                </Typography.Title>
                                <Typography.Text className="text-orange-100 text-sm">
                                    Admin Panel
                                </Typography.Text>
                            </div>
                        )}
                    </div>
                </div>
                
                <Menu
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={menuItems}
                    className="border-r-0 bg-white admin-menu"
                    style={{ 
                        height: 'calc(100vh - 100px)',
                        overflowY: 'auto',
                        background: '#ffffff',
                        padding: '8px 0'
                    }}
                />
            </Sider>
            
            <Layout className="">
            <Header className="bg-white shadow-sm border-b border-gray-200 px-6 flex items-center justify-between h-16" style={{background: 'white !important'}}>
            {/* Left: Collapse Button & Title */}
            <div className="flex items-center space-x-4">
                <Button
                    type="text"
                    icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    onClick={() => setCollapsed(!collapsed)}
                    className="text-gray-600 hover:text-gray-900"
                />
                <Typography.Title level={4} className="!mb-0 !text-gray-900">
                    Admin Panel
                </Typography.Title>
            </div>

            {/* Right: Notifications & Profile Dropdown */}
            <div className="flex items-center space-x-4">
                {/* Unified Notification Center */}
                {/* Reuse the same bell as the main header to avoid duplication and keep behavior consistent */}
                <div className="hidden md:block">
                    {/* NotificationCenter is in main header; admin can rely on that or we can import it here if needed */}
                </div>

                {/* Profile Dropdown */}
                <Dropdown
                    menu={{ items: userMenuItems }}
                    placement="bottomRight"
                    arrow
                    trigger={['click']}
                >
                    <div
                        role="button"
                        tabIndex={0}
                        className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors"
                    >
                        <Avatar
                            icon={<UserOutlined />}
                            size="small"
                            className="bg-indigo-100 text-indigo-600"
                        />
                        <div className="hidden md:block leading-tight">
                            <Typography.Text strong className="text-sm text-gray-900">
                                {user?.name || 'Admin'}
                            </Typography.Text>
                            <br />
                            <Typography.Text type="secondary" className="text-xs text-gray-500">
                                Administrator
                            </Typography.Text>
                        </div>
                    </div>
                </Dropdown>
            </div>
        </Header>
                <Content className="admin-content">
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
}

export default AdminLayout;
